// SolyticsTabs.jsx
// @ts-nocheck
/* eslint-disable react/prop-types */
import React, { useState, useCallback, useMemo } from 'react';
import { Tabs, Tab } from '@mui/material';
import SolyticsBox from './SolyticsBox';
import SolyticsTypography from './SolyticsTypography';
import SolyticsDivider from './SolyticsDivider';

const DEFAULT_VARIANT = 'underlined';
const DEFAULT_SIZE = 'medium';
const DEFAULT_FULL_WIDTH = false;
const DEFAULT_ORIENTATION = 'horizontal';
const DEFAULT_CENTERED = false;
const DEFAULT_SCROLL_BUTTONS = 'auto';
const DEFAULT_SELECTION_FOLLOWS_FOCUS = false;
const DEFAULT_TEXT_COLOR = 'primary';
const DEFAULT_INDICATOR_COLOR = 'primary';

const VARIANTS = {
  SEGMENTED: 'segmented',
  SEGMENTED_2: 'segmented_2',
  UNDERLINED: 'underlined',
  PLAIN: 'plain',
  CONTAINED: 'contained',
  HIGHLIGHTED_1: 'highlighted_1',
  HIGHLIGHTED_2: 'highlighted_2',
  HIGHLIGHTED_3: 'highlighted_3',
};

const ORIENTATIONS = {
  HORIZONTAL: 'horizontal',
  VERTICAL: 'vertical',
};

const SIZES = {
  SMALL: 'small',
  MEDIUM: 'medium',
};

/**
 * Scroll + width styles shared with contained (required for scrollable overflow).
 */
const getContainedScrollStyles = () => ({
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  '& .MuiTabScrollButton-root': {
    minWidth: 32,
    width: 32,
  },
  '& .MuiTabScrollButton-root.Mui-disabled': {
    minWidth: 0,
    width: 0,
    padding: 0,
    overflow: 'hidden',
  },
});

/**
 * Get base styles common to all variants
 */
const getBaseContainerStyles = (size, orientation) => {
  if (orientation === ORIENTATIONS.VERTICAL) {
    return {
      minHeight: 'auto',
      minWidth: size === SIZES.SMALL ? 120 : 160,
    };
  }

  return {
    minHeight: size === SIZES.SMALL ? 36 : 48,
    minWidth: 'auto',
  };
};

/**
 * Get flex container styles based on orientation
 */
const getFlexContainerStyles = (orientation) => ({
  gap: 0,
  position: 'relative',
  zIndex: 1,
  ...(orientation === ORIENTATIONS.VERTICAL && {
    flexDirection: 'column',
  }),
});

/**
 * Get variant-specific styles for tabs container using switch case
 */
const getTabsContainerStyles =
  (variant, size, orientation, fullWidth, containerBorderColor) => (theme) => {
    const baseStyles = getBaseContainerStyles(size, orientation);
    const isVertical = orientation === ORIENTATIONS.VERTICAL;
    const padding = size === SIZES.SMALL ? '4px' : '6px';
    const resolvedContainerBorderColor =
      containerBorderColor || theme?.palette?.divider;

    switch (variant) {
      case VARIANTS.SEGMENTED:
        return {
          ...baseStyles,
          backgroundColor: theme.palette.grey[40],
          borderRadius: fullWidth ? 0 : '4px',
          padding: isVertical ? `0 ${padding}` : padding,
          position: 'relative',
          width: fullWidth ? '100%' : 'fit-content',
          '& .MuiTabScrollButton-root': {
            display: 'none',
          },
          '& .MuiTabs-indicator': {
            display: 'block',
            backgroundColor: theme.palette.background.default,
            borderRadius: '4px',
            boxShadow:
              '0 1px 3px 0 rgba(0, 0, 0, 0.10), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: 0,
            ...(isVertical
              ? {
                  width: '100%',
                  height: 'auto',
                  top: 'auto',
                  bottom: 'auto',
                }
              : {
                  height: '100%',
                  width: 'auto',
                  left: 'auto',
                  right: 'auto',
                }),
          },
          '& .MuiTabs-scroller': {
            overflow:
              orientation === 'vertical'
                ? 'visible !important'
                : 'hidden !important',
          },
          '& .MuiTabs-list': getFlexContainerStyles(orientation),
        };

      case VARIANTS.SEGMENTED_2:
        return {
          ...baseStyles,
          ...getContainedScrollStyles(),
          minHeight: 38,
          height: 38,
          maxHeight: 38,
          backgroundColor: theme.palette.grey[40],
          borderRadius: fullWidth ? 0 : '8px',
          border: '0px !important',
          padding: '4px 8px',
          position: 'relative',
          overflow: 'visible',
          boxSizing: 'border-box',
          '& .MuiTabs-scroller': {
            overflowY: 'visible !important',
            minHeight: 30,
            height: 30,
            maxHeight: 30,
          },
          '& .MuiTabs-indicator': {
            display: 'block',
            backgroundColor: theme?.palette?.background?.default1,
            borderRadius: '6px',
            boxShadow:
              '0 1px 3px 0 rgba(0, 0, 0, 0.10), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: 0,
            ...(isVertical
              ? {
                  width: 'calc(100% - 4px) !important',
                  height: 'auto',
                  top: 'auto',
                  bottom: 'auto',
                  left: '2px !important',
                  right: 'auto',
                }
              : {
                  height: 'calc(100% - 4px) !important',
                  top: '2px !important',
                  bottom: 'auto',
                  width: 'auto',
                  left: 'auto',
                  right: 'auto',
                }),
          },
          '& .MuiTabs-list': {
            ...getFlexContainerStyles(orientation),
            gap: theme.spacing(2),
            justifyContent: 'flex-start',
            minHeight: 30,
            height: 30,
            maxHeight: 30,
          },
        };

      case VARIANTS.UNDERLINED:
        return {
          ...baseStyles,
          ...(isVertical
            ? {
                borderRight: `1px solid ${theme.palette.grey[40]}`,
                '& .MuiTabs-indicator': {
                  width: 2,
                  right: 0,
                  left: 'auto',
                  borderRadius: '0 2px 2px 0',
                  backgroundColor: theme.palette.primary.main,
                  transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                },
              }
            : {
                borderBottom: `1px solid ${theme.palette.grey[40]}`,
                '& .MuiTabs-indicator': {
                  height: 2,
                  backgroundColor: theme.palette.primary.main,
                  transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                },
              }),
          '& .MuiTabs-list': getFlexContainerStyles(orientation),
        };

      case VARIANTS.HIGHLIGHTED_1:
      case VARIANTS.HIGHLIGHTED_2:
        return {
          ...baseStyles,
          backgroundColor: 'transparent',
          borderRadius: fullWidth ? 0 : '4px',
          padding: isVertical ? '0' : padding,
          '& .MuiTabScrollButton-root': {
            display: 'none',
          },
          '& .MuiTabs-indicator': {
            display: 'none',
          },
          '& .MuiTabs-list': {
            ...getFlexContainerStyles(orientation),
          },
        };

      case VARIANTS.HIGHLIGHTED_3:
        return {
          ...baseStyles,
          backgroundColor: theme.palette.background.default,
          border: `1px solid ${resolvedContainerBorderColor}`,
          borderRadius: '4px',
          padding: '0px',
          width: fullWidth ? '100%' : 'fit-content',
          maxWidth: '100%',
          minWidth: 0,
          '& .MuiTabScrollButton-root': {
            minWidth: 32,
            width: 32,
          },
          '& .MuiTabScrollButton-root.Mui-disabled': {
            minWidth: 0,
            width: 0,
            padding: 0,
            overflow: 'hidden',
          },
          '& .MuiTabs-indicator': {
            display: 'block',
            backgroundColor: theme.palette.selection.main,
            height: '100%',
            borderRadius: '4px',
            zIndex: 0,
            transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: 'none !important',
          },
          '& .MuiTabs-list': {
            ...getFlexContainerStyles(orientation),
            gap: theme.spacing(2),
            justifyContent: 'flex-start',
          },
        };

      case VARIANTS.PLAIN:
        return {
          ...baseStyles,
          '& .MuiTabs-indicator': {
            display: 'none',
          },
          '& .MuiTabs-list': getFlexContainerStyles(orientation),
        };

      case VARIANTS.CONTAINED:
        return {
          ...baseStyles,
          backgroundColor: theme.palette.background.default,
          border: `1px solid ${resolvedContainerBorderColor}`,
          borderRadius: '4px',
          padding: '0px',
          width: fullWidth ? '100%' : 'fit-content',
          '& .MuiTabScrollButton-root': {
            minWidth: 32,
            width: 32,
          },
          '& .MuiTabScrollButton-root.Mui-disabled': {
            minWidth: 0,
            width: 0,
            padding: 0,
            overflow: 'hidden',
          },
          '& .MuiTabs-indicator': {
            height: '1.5px',
            bottom: 0,
            backgroundColor: theme.palette.primary.main,
            transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          },
          '& .MuiTabs-list': {
            ...getFlexContainerStyles(orientation),
            gap: theme.spacing(2),
            justifyContent: 'flex-start',
          },
        };

      default:
        return baseStyles;
    }
  };

/**
 * Get base tab styles common to all variants
 */
const getBaseTabStyles = (size, orientation) => {
  const isVertical = orientation === ORIENTATIONS.VERTICAL;
  const fontSize = size === SIZES.SMALL ? '13px' : '14px';
  const minHeight = size === SIZES.SMALL ? 24 : 36;

  return {
    textTransform: 'none',
    fontSize,
    lineHeight: '20px',
    minHeight,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    transition:
      'background-color 300ms cubic-bezier(0.4, 0, 0.2, 1), color 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    '&:focus': {
      outline: 'none',
    },
    '&:active': {
      outline: 'none',
    },
    ...(isVertical && {
      width: '100%',
      textAlign: 'left',
      justifyContent: 'center',
      alignItems: 'stretch',
      height: minHeight,
      maxHeight: minHeight,
      boxSizing: 'border-box',
      marginBottom: isVertical ? '16px' : 0,
      marginRight: isVertical ? 0 : '16px',
      '&:last-of-type': {
        marginBottom: 0,
        marginRight: 0,
      },
    }),
  };
};

/**
 * Get common accessibility and disabled styles
 */
const getAccessibilityStyles = (theme, borderRadius = '6px') => ({
  '&.Mui-disabled': {
    color: theme.palette.text.disabled,
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  '&.Mui-focusVisible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: '2px',
    borderRadius,
  },
  '& .MuiTouchRipple-root': {
    display: 'none',
  },
});

/**
 * Get variant-specific styles for individual tab using switch case
 */
const getTabStyles = (variant, size, orientation, fullWidth) => (theme) => {
  const baseStyles = getBaseTabStyles(size, orientation);
  const isVertical = orientation === ORIENTATIONS.VERTICAL;
  const paddingY = size === SIZES.SMALL ? '6px' : '10px';
  const paddingX = size === SIZES.SMALL ? '12px' : '16px';

  switch (variant) {
    case VARIANTS.SEGMENTED: {
      const marginBottom = isVertical ? '4px' : 0;
      const marginRight = isVertical ? 0 : '4px';
      return {
        ...baseStyles,
        padding: `${paddingY} ${paddingX}`,
        borderRadius: '6px',
        minWidth: 'auto',
        height: '100%',
        backgroundColor: 'transparent',
        color: () => theme.palette.grey.T20,
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '20px',
        marginBottom,
        marginRight,
        '&:last-of-type': {
          marginBottom: 0,
          marginRight: 0,
        },
        '&:hover': {
          backgroundColor: 'transparent',
          color: theme.palette.text.primary,
        },
        '&.Mui-selected': {
          color: () => theme.palette.text.primary,
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '20px',
        },
        ...getAccessibilityStyles(theme, '6px'),
      };
    }

    case VARIANTS.SEGMENTED_2:
      return {
        ...baseStyles,
        padding: '2px 8px',
        minWidth: 'auto',
        minHeight: 30,
        height: 30,
        maxHeight: 30,
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: 1.25,
        color: theme?.palette?.text?.primary,
        borderRadius: 0,
        zIndex: 1,
        '&:hover': {
          color: theme.palette.grey.T80,
          backgroundColor: 'transparent',
        },
        '&.Mui-selected': {
          color: theme?.palette?.primary?.main1,
          fontWeight: 500,
        },
        ...getAccessibilityStyles(theme, '4px'),
      };

    case VARIANTS.UNDERLINED: {
      const marginBottom = isVertical ? '8px' : 0;
      const marginRight = isVertical ? 0 : '16px';
      return {
        ...baseStyles,
        padding: `${paddingY} ${paddingX}`,
        minWidth: 'auto',
        color: () => theme.palette.grey.T10 || theme.palette.text.disabled,
        marginBottom,
        marginRight,
        '&:last-of-type': {
          marginBottom: 0,
          marginRight: 0,
        },
        '&:hover': {
          color: theme.palette.text.primary,
          opacity: 1,
        },
        '&.Mui-selected': {
          color: theme.palette.primary.main,
        },
        ...getAccessibilityStyles(theme, '4px'),
      };
    }

    case VARIANTS.HIGHLIGHTED_1: {
      return {
        ...baseStyles,
        padding: `${paddingY} ${paddingX}`,
        minWidth: 'auto',
        borderRadius: fullWidth ? 0 : '4px',
        backgroundColor: 'transparent',
        color: theme.palette.text.primary,
        marginBottom: 0,
        marginRight: 0,
        '&:hover': {
          backgroundColor: theme.palette.grey[20],
          color: theme.palette.text.primary,
        },
        '&.Mui-selected': {
          backgroundColor: theme.palette.selection.main,
          color: theme.palette.text.primary,
          fontWeight: 500,
        },
        ...getAccessibilityStyles(theme, '6px'),
      };
    }

    case VARIANTS.HIGHLIGHTED_2: {
      return {
        ...baseStyles,
        padding: `${paddingY} ${paddingX}`,
        minWidth: 'auto',
        borderRadius: fullWidth ? 0 : '4px',
        backgroundColor: 'transparent',
        color: theme.palette.text.primary,
        marginBottom: 0,
        marginRight: 0,
        '&:hover': {
          backgroundColor: theme.palette.grey[20],
          color: theme.palette.text.primary,
        },
        '&.Mui-selected': {
          backgroundColor: theme.palette.selection.secondary,
          color: theme.palette.text.primary,
          fontWeight: 500,
        },
        ...getAccessibilityStyles(theme, '6px'),
      };
    }

    case VARIANTS.HIGHLIGHTED_3: {
      return {
        ...baseStyles,
        padding: '2px 8px',
        minWidth: 'auto',
        borderRadius: 0,
        backgroundColor: 'transparent',
        color: theme.palette.grey.T6,
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: 1.25,
        height: '100%',

        '&.Mui-selected': {
          backgroundColor: 'transparent',
          color: theme.palette.primary.main,
          fontWeight: 500,
        },
        ...getAccessibilityStyles(theme, '2px'),
      };
    }

    case VARIANTS.PLAIN: {
      const marginBottom = isVertical ? '8px' : 0;
      const marginRight = isVertical ? 0 : '16px';
      return {
        ...baseStyles,
        padding: `${paddingY} ${paddingX}`,
        minWidth: 'auto',
        color: theme.palette.grey.T10 || theme.palette.text.disabled,
        marginBottom,
        marginRight,
        '&:last-of-type': {
          marginBottom: 0,
          marginRight: 0,
        },
        '&:hover': {
          color: theme.palette.text.primary,
          backgroundColor: 'transparent',
        },
        '&.Mui-selected': {
          color: theme.palette.text.primary,
          backgroundColor: 'transparent',
        },
        ...getAccessibilityStyles(theme, '4px'),
      };
    }

    case VARIANTS.CONTAINED: {
      return {
        ...baseStyles,
        padding: '2px 8px',
        minWidth: 'auto',
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: 1.25,
        color: theme.palette.text.secondary,
        borderRadius: 0,
        '&:hover': {
          color: theme.palette.text.primary,
          backgroundColor: 'transparent',
        },
        '&.Mui-selected': {
          color: theme.palette.primary.main,
          fontWeight: 500,
        },
        ...getAccessibilityStyles(theme, '4px'),
      };
    }

    default:
      return baseStyles;
  }
};

/**
 * SolyticsTabs Component
 *
 * A flexible tabs component with multiple variants and full accessibility support.
 *
 * @param {Object} props - Component props
 * @param {Array<TabItem>} props.items - Array of tab items with structure:
 *   - label: string (tab label text)
 *   - value: string|number (unique identifier)
 *   - content: ReactNode (tab panel content)
 *   - disabled: boolean (optional)
 *   - badge: number|string (optional, for highlighted variant)
 * @param {string} [props.variant='underlined'] - Tab variant:
 *   - 'segmented': Box-style with background indicator
 *   - 'segmented_2': Contained-like scroll/tabs with pill track and sliding white indicator
 *   - 'underlined': Standard with bottom border indicator
 *   - 'plain': Minimalist without indicator
 *   - 'highlighted_1': With badge counts (like filter chips)
 *   - 'highlighted_2': Chip-style with selection.secondary background on selection
 *   - 'highlighted_3': Bordered container with selection background on active tab
 *   - 'contained': Bordered container with bottom border on active tab
 * @param {string|number} [props.value] - Controlled value
 * @param {string|number} [props.defaultValue] - Default value for uncontrolled mode
 * @param {function} [props.onChange] - Change handler: (value: string | number) => void
 * @param {boolean} [props.fullWidth=false] - Whether tabs take full width
 * @param {string} [props.size='medium'] - Tab size: 'small' | 'medium'
 * @param {string} [props.orientation='horizontal'] - Tab orientation: 'horizontal' | 'vertical'
 *
 * @example
 * // Basic usage with highlighted_1 variant
 * <SolyticsTabs
 *   variant="highlighted_1"
 *   items={[
 *     { label: 'Model Name', value: 'model', badge: 3, content: <div>...</div> },
 *     { label: 'Tier', value: 'tier', badge: 1, content: <div>...</div> }
 *   ]}
 * />
 */
const SolyticsTabs = ({
  items = [],
  variant = DEFAULT_VARIANT,
  value: controlledValue,
  defaultValue,
  onChange,
  tabsVariant = 'scrollable',
  fullWidth = DEFAULT_FULL_WIDTH,
  size = DEFAULT_SIZE,
  orientation = DEFAULT_ORIENTATION,
  separator = false,
  tabBarPaddingX,
  containerBorderColor,
  showDefaultToolTip = true,
}) => {
  const isControlled = controlledValue !== undefined;

  const [internalValue, setInternalValue] = useState(() => {
    if (defaultValue !== undefined) return defaultValue;
    if (items.length > 0) {
      const firstValue = items[0] && items[0].value;
      return firstValue !== undefined && firstValue !== null ? firstValue : 0;
    }
    return 0;
  });

  const currentValue = isControlled ? controlledValue : internalValue;

  /**
   * Handle tab change
   * @param {Event} event - Change event
   * @param {string|number} newValue - New tab value
   */
  const handleChange = useCallback(
    (event, newValue) => {
      if (!isControlled) {
        setInternalValue(newValue);
      }

      if (onChange) {
        onChange(newValue);
      }
    },
    [isControlled, onChange]
  );

  const tabContainerStyles = useMemo(
    () =>
      getTabsContainerStyles(
        variant,
        size,
        orientation,
        fullWidth,
        containerBorderColor
      ),
    [variant, size, orientation, fullWidth, containerBorderColor]
  );
  const memoTabStyles = useMemo(
    () => getTabStyles(variant, size, orientation, fullWidth),
    [variant, size, orientation, fullWidth]
  );

  const isSegmented2 = variant === VARIANTS.SEGMENTED_2;
  const isSegmented2Scrollable = isSegmented2 && tabsVariant === 'scrollable';

  let selectedItem = items.find((item) => {
    const itemValue =
      item.value !== undefined ? item.value : items.indexOf(item);
    return itemValue === currentValue;
  });

  if (!items || items.length === 0) {
    return null;
  }

  if (!selectedItem && items.length > 0) {
    selectedItem = items[0];
  }

  const tabsProps = {
    value: currentValue,
    onChange: handleChange,
    'aria-label': 'tabs navigation',
    variant: tabsVariant,
    scrollButtons: DEFAULT_SCROLL_BUTTONS,
    allowScrollButtonsMobile: true,
    selectionFollowsFocus: DEFAULT_SELECTION_FOLLOWS_FOCUS,
    orientation: orientation,
    centered: DEFAULT_CENTERED,
    textColor: variant === 'underlined' ? DEFAULT_TEXT_COLOR : 'inherit',
    indicatorColor:
      variant === 'underlined' ? DEFAULT_INDICATOR_COLOR : 'primary',
    sx: tabContainerStyles,
  };

  const tabBar = (
    <Tabs {...tabsProps}>
      {items.map((item, index) => {
        const tabValue = item.value !== undefined ? item.value : index;

        const tabLabel = item.icon ? (
          <SolyticsBox
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            {item.icon}
            <SolyticsTypography variant="subtitle2" color="inherit" noWrap>
              {item.label}
            </SolyticsTypography>
          </SolyticsBox>
        ) : (
          <SolyticsTypography variant="subtitle2" color="inherit" noWrap>
            {item.label}
          </SolyticsTypography>
        );

        const tabProps = {
          label: tabLabel,
          value: tabValue,
          disabled: item.disabled || false,
          disableRipple: false,
          sx: [
            memoTabStyles,
            fullWidth &&
              variant !== VARIANTS.CONTAINED &&
              variant !== VARIANTS.SEGMENTED_2 && {
                flex: 1,
                maxWidth: 'none',
              },
          ],
          'aria-controls': `tabpanel-${tabValue}`,
          id: `tab-${tabValue}`,
        };

        return (
          <Tab
            key={tabValue}
            {...tabProps}
            title={showDefaultToolTip ? item.label : undefined}
          />
        );
      })}
    </Tabs>
  );

  const tabBarNode =
    tabBarPaddingX !== undefined ? (
      <SolyticsBox sx={{ px: tabBarPaddingX }}>{tabBar}</SolyticsBox>
    ) : (
      tabBar
    );

  const tabBarWithLayout = isSegmented2 ? (
    <SolyticsBox
      sx={{
        overflow: 'visible',
        ...(isSegmented2Scrollable && {
          flex: 1,
          minWidth: 0,
          maxWidth: '100%',
        }),
      }}
    >
      {tabBarNode}
    </SolyticsBox>
  ) : (
    tabBarNode
  );

  return (
    <SolyticsBox
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: orientation === 'vertical' ? 'row' : 'column',
        gap: 2,
        ...(isSegmented2Scrollable &&
          !isSegmented2 && {
            flex: 1,
            minWidth: 0,
            maxWidth: '100%',
          }),
        overflow: isSegmented2 ? 'visible' : 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
      }}
    >
      {tabBarWithLayout}
      {separator && (
        <SolyticsDivider
          orientation={orientation === 'vertical' ? 'vertical' : 'horizontal'}
          flexItem
        />
      )}
      {selectedItem && selectedItem?.content && (
        <SolyticsBox
          role="tabpanel"
          id={`tabpanel-${currentValue}`}
          aria-labelledby={`tab-${currentValue}`}
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
          }}
        >
          {selectedItem.content}
        </SolyticsBox>
      )}
    </SolyticsBox>
  );
};

export default SolyticsTabs;
